import React, { useRef } from "react"
import { IListingResponseModel } from "../../../core/models"
import clsx from "clsx"
import { EditableRow } from "../../types"

interface Props {
     rows: EditableRow[]
     selectedRowIds: string[]
     factureFilesMap: Record<string, File> // Un seul fichier par ligne
     expenseFilesMap: Record<string, File[]> // Plusieurs fichiers autorisés
     onToggleSelectAll: (checked: boolean) => void
     onToggleRow: (id: string) => void
     onUpdateRow: (id: string, field: keyof EditableRow, value: any) => void
     onRemoveRow: (id: string) => void
     // Handlers pour la facture UNIQUE
     onAttachFacture: (rowId: string, files: FileList | null) => void
     onRemoveFactureFile: (rowId: string) => void
     onPreviewFactureFile: (rowId: string, file: File) => void
     // Handlers pour les justificatifs MULTIPLES
     onAttachExpenses: (rowId: string, files: FileList | null) => void
     onRemoveExpenseFile: (rowId: string, index: number) => void
     onPreviewExpenseFile: (rowId: string, file: File) => void
     consultants: IListingResponseModel | undefined
     onSave: () => void
     isSaving: boolean
}

const Step3ExtractionTable = ({
     rows,
     selectedRowIds,
     factureFilesMap,
     expenseFilesMap,
     onToggleSelectAll,
     onToggleRow,
     onUpdateRow,
     onRemoveRow,
     onAttachFacture,
     onRemoveFactureFile,
     onPreviewFactureFile,
     onAttachExpenses,
     onRemoveExpenseFile,
     onPreviewExpenseFile,
     consultants,
     onSave,
     isSaving,
}: Props) => {
     if (!rows || rows.length === 0) return null

     /**
      * Logique de validation technique par ligne
      */
     const getRowValidation = (row: EditableRow) => {
          const hasFacture = !!factureFilesMap[row.id]
          const expenseFiles = expenseFilesMap[row.id] || []

          const prestaHtInvalid = !row.prestationHt || Number(row.prestationHt) <= 0
          const prestaVatInvalid = (row.prestationVat as any) === "" || Number(row.prestationVat) < 0

          // Une facture de prestation est obligatoire pour valider la ligne
          const factureMissing = !hasFacture

          const hasJustificatifs = expenseFiles.length > 0
          const hasFraisHt = Number(row.fraisHt) > 0

          // Validation des frais : cohérence entre montants et fichiers
          const fraisFilesInvalid = (hasFraisHt || ((row.fraisVat as any) !== "" && Number(row.fraisVat) > 0)) && !hasJustificatifs
          const fraisHtInvalid = hasJustificatifs && (!row.fraisHt || Number(row.fraisHt) <= 0)
          const fraisVatInvalid = hasJustificatifs && (row.fraisVat as any) === ""

          // Doublons autorisés côté métier
          const isDuplicate = false

          return {
               prestaHtInvalid,
               prestaVatInvalid,
               fraisHtInvalid,
               fraisVatInvalid,
               fraisFilesInvalid,
               factureMissing,
               isDuplicate,
               isSelectable: !prestaHtInvalid && !prestaVatInvalid && !fraisHtInvalid && !fraisVatInvalid && !fraisFilesInvalid && !factureMissing && !!row.selectedConsultantId,
          }
     }

     const selectableRows = rows.filter(r => getRowValidation(r).isSelectable)
     const selectedCount = selectedRowIds.length
     const isAllSelected = selectableRows.length > 0 && selectableRows.every(r => selectedRowIds.includes(r.id))
     const isSaveDisabled = isSaving || selectedCount === 0 || rows.some(r => selectedRowIds.includes(r.id) && !getRowValidation(r).isSelectable)

     return (
          <div className="card shadow-sm border border-gray-200 animate__animated animate__fadeInUp">
               <div className="card-header align-items-center py-5 border-0">
                    <div className="card-title flex-column">
                         <h3 className="fw-bolder text-gray-900 fs-2 mb-1">3. Validation des données et justificatifs</h3>
                         <div className="text-muted fw-semibold fs-7">Vérifiez les montants et ajoutez les justificatifs.</div>
                    </div>

                    <div
                         className={clsx(
                              "d-lg-none d-flex align-items-center py-4 px-6 rounded-3 shadow-sm transition-all w-100 mt-4",
                              "bg-light-primary bg-opacity-10 border border-2 border-primary border-opacity-50",
                              selectableRows.length === 0 ? "opacity-50" : "cursor-pointer"
                         )}
                         onClick={() => selectableRows.length > 0 && onToggleSelectAll(!isAllSelected)}
                    >
                         <input
                              className="form-check-input h-22px w-22px shadow-sm cursor-pointer border-primary m-0 me-4"
                              type="checkbox"
                              checked={isAllSelected}
                              disabled={selectableRows.length === 0}
                              onChange={e => onToggleSelectAll(e.target.checked)}
                              onClick={e => e.stopPropagation()}
                         />
                         <span className="fw-boldest text-primary fs-6">Tout sélectionner ({selectableRows.length} valides)</span>
                    </div>
               </div>

               <div className="card-body p-0">
                    <div className="d-none d-lg-block table-responsive">
                         <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
                              <thead>
                                   <tr className="fw-bold text-muted bg-light text-uppercase fs-8">
                                        <th className="w-50px ps-4 text-center"></th>
                                        <th className="min-w-150px">Consultant</th>
                                        <th className="w-80px text-center">Jours</th>
                                        <th className="w-180px text-center text-primary bg-light-primary bg-opacity-10">Prestation (HT / TVA)</th>
                                        <th className="w-180px text-center text-warning bg-light-warning bg-opacity-10">Frais (HT / TVA)</th>
                                        <th className="w-100px text-center">Total HT</th>
                                        <th className="w-100px text-center">Facture Presta</th>
                                        <th className="w-120px text-center">Justificatifs Frais</th>
                                        <th className="w-50px pe-4 text-end">
                                             <input
                                                  className="form-check-input h-18px w-18px"
                                                  type="checkbox"
                                                  checked={isAllSelected}
                                                  disabled={selectableRows.length === 0}
                                                  onChange={e => onToggleSelectAll(e.target.checked)}
                                             />
                                        </th>
                                   </tr>
                              </thead>
                              <tbody>
                                   {rows.map(row => {
                                        const v = getRowValidation(row)
                                        const isSelected = selectedRowIds.includes(row.id)
                                        const rowTotalHt = (Number(row.prestationHt) || 0) + (Number(row.fraisHt) || 0)

                                        return (
                                             <tr key={row.id} className={clsx("transition-all", isSelected && "bg-light-primary bg-opacity-25")}>
                                                  <td className="ps-4 text-center">
                                                       <button className="btn btn-icon btn-sm btn-active-light-danger" onClick={() => onRemoveRow(row.id)} disabled={isSaving}>
                                                            <i className="fas fa-trash text-gray-400"></i>
                                                       </button>
                                                  </td>
                                                  <td>
                                                       <select
                                                            className={clsx("form-select form-select-sm fw-bold", !row.selectedConsultantId && "border-danger border-2")}
                                                            value={row.selectedConsultantId || ""}
                                                            onChange={e => onUpdateRow(row.id, "selectedConsultantId", e.target.value ? Number(e.target.value) : null)}
                                                       >
                                                            <option value="">-- Sélectionner --</option>
                                                            {consultants?.data.map((item: any) => (
                                                                 <option key={item.consultant.id} value={item.consultant.id!}>
                                                                      {item.consultant.prenomNom}
                                                                 </option>
                                                            ))}
                                                       </select>
                                                  </td>
                                                  <td>
                                                       <input
                                                            type="number"
                                                            step="0.5"
                                                            className="form-control form-control-sm text-center"
                                                            value={row.nbJoursTravailles ?? ""}
                                                            onChange={e => onUpdateRow(row.id, "nbJoursTravailles", e.target.value === "" ? "" : Number(e.target.value))}
                                                       />
                                                  </td>
                                                  <td className="bg-light-primary bg-opacity-5">
                                                       <div className="d-flex gap-1">
                                                            <input
                                                                 type="number"
                                                                 className={clsx("form-control form-control-sm text-center", v.prestaHtInvalid && "border-danger border-2")}
                                                                 placeholder="HT"
                                                                 value={row.prestationHt ?? ""}
                                                                 onChange={e => onUpdateRow(row.id, "prestationHt", e.target.value === "" ? "" : Number(e.target.value))}
                                                            />
                                                            <input
                                                                 type="number"
                                                                 className={clsx("form-control form-control-sm text-center", v.prestaVatInvalid && "border-danger border-2")}
                                                                 placeholder="TVA"
                                                                 value={row.prestationVat ?? ""}
                                                                 onChange={e => onUpdateRow(row.id, "prestationVat", e.target.value === "" ? "" : Number(e.target.value))}
                                                            />
                                                       </div>
                                                  </td>
                                                  <td className="bg-light-warning bg-opacity-5">
                                                       <div className="d-flex gap-1">
                                                            <input
                                                                 type="number"
                                                                 className={clsx("form-control form-control-sm text-center", v.fraisHtInvalid && "border-danger border-2")}
                                                                 placeholder="HT"
                                                                 value={row.fraisHt ?? ""}
                                                                 onChange={e => onUpdateRow(row.id, "fraisHt", e.target.value === "" ? "" : Number(e.target.value))}
                                                            />
                                                            <input
                                                                 type="number"
                                                                 className={clsx("form-control form-control-sm text-center", v.fraisVatInvalid && "border-danger border-2")}
                                                                 placeholder="TVA"
                                                                 value={row.fraisVat ?? ""}
                                                                 onChange={e => onUpdateRow(row.id, "fraisVat", e.target.value === "" ? "" : Number(e.target.value))}
                                                            />
                                                       </div>
                                                  </td>
                                                  <td className="text-center fw-boldest text-gray-800">{rowTotalHt.toFixed(2)}€</td>
                                                  <td className="px-2 text-center">
                                                       <SingleFileDropzone
                                                            rowId={row.id}
                                                            file={factureFilesMap[row.id]}
                                                            onAttach={onAttachFacture}
                                                            onRemove={onRemoveFactureFile}
                                                            onPreview={onPreviewFactureFile}
                                                            validation={{ error: v.factureMissing }}
                                                            icon="fas fa-file-invoice"
                                                            color="primary"
                                                       />
                                                  </td>
                                                  <td className="px-2 text-center">
                                                       <MultiFileDropzone
                                                            rowId={row.id}
                                                            files={expenseFilesMap[row.id] || []}
                                                            onAttach={onAttachExpenses}
                                                            onRemove={onRemoveExpenseFile}
                                                            onPreview={onPreviewExpenseFile}
                                                            validation={{ error: v.fraisFilesInvalid }}
                                                            icon="fas fa-paperclip"
                                                            color="success"
                                                       />
                                                  </td>
                                                  <td className="pe-4 text-end">
                                                       <input
                                                            className="form-check-input h-18px w-18px"
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            disabled={!v.isSelectable}
                                                            onChange={() => onToggleRow(row.id)}
                                                       />
                                                  </td>
                                             </tr>
                                        )
                                   })}
                              </tbody>
                         </table>
                    </div>

                    <div className="d-lg-none p-5">
                         {rows.map(row => {
                              const v = getRowValidation(row)
                              const isSelected = selectedRowIds.includes(row.id)
                              const rowTotalHt = (Number(row.prestationHt) || 0) + (Number(row.fraisHt) || 0)

                              return (
                                   <div
                                        key={row.id}
                                        className={clsx(
                                             "border rounded-3 p-5 mb-5 shadow-sm transition-all",
                                             isSelected ? "border-primary bg-light-primary bg-opacity-15" : "bg-body",
                                             v.isSelectable ? "cursor-pointer" : "opacity-85"
                                        )}
                                        onClick={() => v.isSelectable && onToggleRow(row.id)}
                                   >
                                        <div className="d-flex justify-content-between align-items-center mb-4">
                                             <div className="d-flex align-items-center gap-3 flex-grow-1">
                                                  <input
                                                       className="form-check-input h-20px w-20px shadow-sm"
                                                       type="checkbox"
                                                       checked={isSelected}
                                                       disabled={!v.isSelectable}
                                                       onChange={() => onToggleRow(row.id)}
                                                       onClick={e => e.stopPropagation()}
                                                  />
                                                  <select
                                                       className={clsx("form-select form-select-sm fw-bold border", !row.selectedConsultantId && "border-danger border-2")}
                                                       value={row.selectedConsultantId || ""}
                                                       onChange={e => onUpdateRow(row.id, "selectedConsultantId", e.target.value ? Number(e.target.value) : null)}
                                                       onClick={e => e.stopPropagation()}
                                                  >
                                                       <option value="">-- Consultant --</option>
                                                       {consultants?.data.map((item: any) => (
                                                            <option key={item.consultant.id} value={item.consultant.id!}>
                                                                 {item.consultant.prenomNom}
                                                            </option>
                                                       ))}
                                                  </select>
                                             </div>
                                             <button
                                                  className="btn btn-icon btn-sm btn-light-danger"
                                                  onClick={e => {
                                                       e.stopPropagation()
                                                       onRemoveRow(row.id)
                                                  }}
                                             >
                                                  <i className="fas fa-trash"></i>
                                             </button>
                                        </div>

                                        <div className="row g-3">
                                             <div className="col-12 bg-light-primary bg-opacity-10 rounded p-4" onClick={e => e.stopPropagation()}>
                                                  <div className="d-flex justify-content-between align-items-center mb-3">
                                                       <label className="fs-9 text-primary fw-bold text-uppercase m-0">Prestation & Facture</label>
                                                       <SingleFileDropzone
                                                            rowId={row.id}
                                                            file={factureFilesMap[row.id]}
                                                            onAttach={onAttachFacture}
                                                            onRemove={onRemoveFactureFile}
                                                            onPreview={onPreviewFactureFile}
                                                            validation={{ error: v.factureMissing }}
                                                            icon="fas fa-file-invoice"
                                                            color="primary"
                                                       />
                                                  </div>
                                                  <div className="d-flex gap-2">
                                                       <input
                                                            type="number"
                                                            step="0.5"
                                                            className="form-control form-control-sm text-center w-60px h-40px fs-6"
                                                            value={row.nbJoursTravailles ?? ""}
                                                            onChange={e => onUpdateRow(row.id, "nbJoursTravailles", e.target.value === "" ? "" : Number(e.target.value))}
                                                       />
                                                       <input
                                                            type="number"
                                                            className={clsx("form-control form-control-sm text-center h-40px fs-6", v.prestaHtInvalid && "border-danger border-2")}
                                                            placeholder="HT"
                                                            value={row.prestationHt ?? ""}
                                                            onChange={e => onUpdateRow(row.id, "prestationHt", e.target.value === "" ? "" : Number(e.target.value))}
                                                       />
                                                       <input
                                                            type="number"
                                                            className={clsx("form-control form-control-sm text-center h-40px fs-6", v.prestaVatInvalid && "border-danger border-2")}
                                                            placeholder="TVA"
                                                            value={row.prestationVat ?? ""}
                                                            onChange={e => onUpdateRow(row.id, "prestationVat", e.target.value === "" ? "" : Number(e.target.value))}
                                                       />
                                                  </div>
                                             </div>

                                             <div className="col-12 bg-light-warning bg-opacity-10 rounded p-4" onClick={e => e.stopPropagation()}>
                                                  <div className="d-flex justify-content-between align-items-center mb-3">
                                                       <label className="fs-9 text-warning fw-bold text-uppercase m-0">Frais & Justificatifs</label>
                                                       <MultiFileDropzone
                                                            rowId={row.id}
                                                            files={expenseFilesMap[row.id] || []}
                                                            onAttach={onAttachExpenses}
                                                            onRemove={onRemoveExpenseFile}
                                                            onPreview={onPreviewExpenseFile}
                                                            validation={{ error: v.fraisFilesInvalid }}
                                                            icon="fas fa-paperclip"
                                                            color="success"
                                                       />
                                                  </div>
                                                  <div className="d-flex gap-2">
                                                       <input
                                                            type="number"
                                                            className={clsx("form-control form-control-sm text-center h-40px fs-6", v.fraisHtInvalid && "border-danger border-2")}
                                                            placeholder="HT"
                                                            value={row.fraisHt ?? ""}
                                                            onChange={e => onUpdateRow(row.id, "fraisHt", e.target.value === "" ? "" : Number(e.target.value))}
                                                       />
                                                       <input
                                                            type="number"
                                                            className={clsx("form-control form-control-sm text-center h-40px fs-6", v.fraisVatInvalid && "border-danger border-2")}
                                                            placeholder="TVA"
                                                            value={row.fraisVat ?? ""}
                                                            onChange={e => onUpdateRow(row.id, "fraisVat", e.target.value === "" ? "" : Number(e.target.value))}
                                                       />
                                                  </div>
                                             </div>

                                             <div className="col-12 d-flex justify-content-end align-items-center pt-2">
                                                  <div className="fs-8 fw-bold text-gray-500 me-2">TOTAL HT :</div>
                                                  <div className="fs-5 fw-boldest text-primary">{rowTotalHt.toFixed(2)}€</div>
                                             </div>
                                        </div>
                                   </div>
                              )
                         })}
                    </div>

                    <div className="card-footer border-0 d-flex flex-column flex-sm-row justify-content-between align-items-center py-8">
                         {rows.some(r => !getRowValidation(r).isSelectable) ? (
                              <div className="text-danger fw-bold fs-7 mb-4 mb-sm-0 animate__animated animate__headShake">
                                   <i className="fas fa-exclamation-triangle me-2 text-danger"></i>
                                   Erreurs de saisie (montant ou document manquant).
                              </div>
                         ) : (
                              <div className="text-gray-600 fs-7 fw-bold mb-4 mb-sm-0">
                                   {selectedCount === 0 ? (
                                        <span className="text-danger fw-boldest">Veuillez cocher au moins une ligne ( la checkbox est à droite de la rangée )</span>
                                   ) : (
                                        `${selectedCount} ligne(s) sélectionnée(s) sur ${rows.length}`
                                   )}
                              </div>
                         )}
                         <button className="btn btn-primary btn-lg px-20 shadow-sm" onClick={onSave} disabled={isSaveDisabled}>
                              {isSaving ? "Envoi en cours ..." : "Valider et envoyer"}
                         </button>
                    </div>
               </div>
          </div>
     )
}

/**
 * Composant pour un fichier unique (Facture)
 */
const SingleFileDropzone = ({ rowId, file, onAttach, onRemove, onPreview, validation, icon, color }: any) => {
     const inputRef = useRef<HTMLInputElement>(null)
     const hasFile = !!file

     return (
          <div
               className={clsx(
                    "d-flex align-items-center justify-content-center transition-all",
                    !hasFile && "bg-light-warning border border-warning rounded p-1",
                    validation.error && "border-danger border-2 pulse pulse-danger"
               )}
               style={{ minWidth: "40px", minHeight: "35px" }}
          >
               {hasFile ? (
                    <div className="position-relative animate__animated animate__fadeIn">
                         <div className={clsx("badge badge-icon p-2 cursor-pointer shadow-sm", `badge-light-${color}`)} onClick={() => onPreview(rowId, file)}>
                              <i className={clsx(icon, "fs-7", `text-${color}`)}></i>
                         </div>
                         <button
                              type="button"
                              className="btn btn-icon btn-xs btn-danger rounded-circle position-absolute top-0 start-100 translate-middle h-15px w-15px shadow"
                              onClick={() => onRemove(rowId)}
                         >
                              <i className="fas fa-times" style={{ fontSize: "6px" }}></i>
                         </button>
                    </div>
               ) : (
                    <>
                         <button
                              className={clsx("btn btn-icon btn-sm rounded-circle shadow-sm", validation.error ? "btn-light-danger" : "btn-light-success")}
                              onClick={() => inputRef.current?.click()}
                         >
                              <i className="fas fa-plus fs-8"></i>
                         </button>
                         <input type="file" ref={inputRef} className="d-none" accept="image/*,application/pdf" onChange={e => onAttach(rowId, e.target.files)} />
                    </>
               )}
          </div>
     )
}

/**
 * Composant pour justificatifs multiples
 */
const MultiFileDropzone = ({ rowId, files, onAttach, onRemove, onPreview, validation, icon, color }: any) => {
     const inputRef = useRef<HTMLInputElement>(null)
     const hasFiles = files.length > 0

     return (
          <div
               className={clsx(
                    "d-flex flex-wrap align-items-center justify-content-center gap-2",
                    !hasFiles && "bg-light-warning border border-warning rounded p-1",
                    validation.error && "border-danger border-2"
               )}
               style={{ minWidth: "40px", minHeight: "35px" }}
          >
               {files.map((file: any, idx: number) => (
                    <div key={idx} className="position-relative">
                         <div className={clsx("badge badge-icon p-2 cursor-pointer", `badge-light-${color}`)} onClick={() => onPreview(rowId, file)}>
                              <i className={clsx(icon, "fs-7", `text-${color}`)}></i>
                         </div>
                         <button
                              type="button"
                              className="btn btn-icon btn-xs btn-danger rounded-circle position-absolute top-0 start-100 translate-middle h-15px w-15px shadow"
                              onClick={e => {
                                   e.stopPropagation()
                                   onRemove(rowId, idx)
                              }}
                         >
                              <i className="fas fa-times" style={{ fontSize: "6px" }}></i>
                         </button>
                    </div>
               ))}
               <button
                    className="btn btn-icon btn-sm btn-light-success rounded-circle shadow-sm"
                    onClick={e => {
                         e.stopPropagation()
                         inputRef.current?.click()
                    }}
               >
                    <i className="fas fa-plus fs-8"></i>
               </button>
               <input type="file" multiple ref={inputRef} className="d-none" accept="image/*,application/pdf" onChange={e => onAttach(rowId, e.target.files)} />
          </div>
     )
}

export default Step3ExtractionTable
